/**
 * RTS camera rig: pan (WASD / arrows / middle-drag / one-finger touch drag),
 * zoom (wheel / pinch), rotate (Q/E, right-drag, or two-finger twist).
 * Target stays glued to the terrain.
 *
 * Drag panning is Google-Maps style by default: the world sticks to the
 * finger/cursor — the terrain under the pointer stays under the pointer.
 * Set `invertPan = true` for the legacy RTS feel (drag moves the camera
 * with the pointer). WASD/arrow panning is unaffected by the toggle.
 */
import * as THREE from "three";

export class RtsCamera {
  target = new THREE.Vector3(0, 0, 0);
  yaw = Math.PI / 4;
  pitch = 0.9; // radians above horizon-plane tilt
  distance = 90;

  /** Flip drag-pan direction (Settings → Camera). Default: world follows finger. */
  invertPan = false;
  /**
   * Pan/zoom speed multiplier (Settings → Camera → Speed, 0.5–2, default 1).
   * Scales drag-pan, wheel/pinch zoom steps, and keyboard pan.
   */
  panSpeed = 1;

  private keys = new Set<string>();
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  /** Camera target clamp, world units; set from the map size at boot. */
  private bounds = 230;

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    dom: HTMLElement,
    private readonly groundY: (x: number, z: number) => number,
  ) {
    window.addEventListener("keydown", (e) => this.keys.add(e.key.toLowerCase()));
    window.addEventListener("keyup", (e) => this.keys.delete(e.key.toLowerCase()));
    dom.addEventListener("wheel", (e) => {
      e.preventDefault();
      this.zoomBy(1 + Math.sign(e.deltaY) * 0.1);
    }, { passive: false });
    dom.addEventListener("mousedown", (e) => {
      if (e.button === 1) {
        this.dragging = true;
        this.lastX = e.clientX;
        this.lastY = e.clientY;
        e.preventDefault();
      }
    });
    window.addEventListener("mousemove", (e) => {
      if (!this.dragging) return;
      const dx = e.clientX - this.lastX;
      const dy = e.clientY - this.lastY;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      this.dragPan(dx, dy);
    });
    window.addEventListener("mouseup", () => (this.dragging = false));
  }

  /**
   * Pan the camera by a screen-space drag delta in CSS pixels.
   * Shared by middle-drag and single-finger touch drag.
   *
   * Default (invertPan = false) is Google-Maps style: the world follows
   * the pointer, so the terrain under the finger stays under the finger
   * (the camera target moves opposite the drag). With invertPan the
   * camera moves with the pointer instead. Note the y sign: panBy's
   * `forward` axis points up-screen, so a downward finger drag needs a
   * positive forward push to keep the world glued to the finger.
   */
  dragPan(dxPx: number, dyPx: number): void {
    const s = this.invertPan ? 1 : -1;
    const k = this.distance * 0.0016 * this.panSpeed;
    this.panBy(s * dxPx * k, -s * dyPx * k);
  }

  /** Rotate the camera around the target (two-finger twist on touch). */
  rotateBy(deltaYaw: number): void {
    this.yaw += deltaYaw;
  }

  /** Set the camera-target clamp from the map's half-extent. */
  setBounds(halfExtent: number): void {
    this.bounds = halfExtent + 30;
  }

  /** Zoom by a multiplicative factor (< 1 zooms in). Shared by wheel + pinch. */
  zoomBy(factor: number): void {
    // panSpeed scales the step size (factor>1 = zoom out, factor<1 = zoom in).
    const stepped = factor > 1
      ? 1 + (factor - 1) * this.panSpeed
      : 1 - (1 - factor) * this.panSpeed;
    this.distance = THREE.MathUtils.clamp(this.distance * stepped, 18, 320);
  }

  /**
   * Pan in screen space: `right` moves the target along the camera's
   * screen-right axis, `forward` along screen-up (away from the camera).
   *
   * With the camera at target + (sin yaw, cos yaw) * d looking at the
   * target, screen-right on the ground is (cos yaw, -sin yaw) and
   * screen-up is (-sin yaw, -cos yaw). (An earlier revision had the
   * z-row signs flipped, which made touch drags slide the world
   * diagonally — the "map scrolls backwards" bug.)
   */
  private panBy(right: number, forward: number): void {
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    const b = this.bounds;
    this.target.x = THREE.MathUtils.clamp(this.target.x + right * cos - forward * sin, -b, b);
    this.target.z = THREE.MathUtils.clamp(this.target.z - right * sin - forward * cos, -b, b);
  }

  update(dt: number): void {
    const speed = this.distance * 0.9 * this.panSpeed;
    let r = 0;
    let f = 0;
    if (this.keys.has("a") || this.keys.has("arrowleft")) r -= 1;
    if (this.keys.has("d") || this.keys.has("arrowright")) r += 1;
    if (this.keys.has("w") || this.keys.has("arrowup")) f += 1;
    if (this.keys.has("s") || this.keys.has("arrowdown")) f -= 1;
    if (r !== 0 || f !== 0) {
      const n = Math.hypot(r, f);
      this.panBy((r / n) * speed * dt, (f / n) * speed * dt);
    }
    if (this.keys.has("q")) this.yaw += 1.4 * dt;
    if (this.keys.has("e")) this.yaw -= 1.4 * dt;

    this.target.y = this.groundY(this.target.x, this.target.z);
    const cp = Math.cos(this.pitch);
    this.camera.position.set(
      this.target.x + Math.sin(this.yaw) * cp * this.distance,
      this.target.y + Math.sin(this.pitch) * this.distance,
      this.target.z + Math.cos(this.yaw) * cp * this.distance,
    );
    this.camera.lookAt(this.target);
  }
}
